import { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useV2Auth } from '@/context/V2AuthContext';
import { useTranslation } from 'react-i18next';

function EmailAuthForm({ mode, redirect }) {
  const navigate = useNavigate();
  const { setUser, setAuthLoading, setError, API_V2, refresh } = useV2Auth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isSignup = mode === 'signup';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setAuthLoading(true);
    setError(null);
    try {
      let res;
      if (isSignup) {
        res = await axios.post(
          `${API_V2}/auth/email/register`,
          { email, password, name: name || email.split('@')[0] },
          { withCredentials: true },
        );
      } else {
        res = await axios.post(
          `${API_V2}/auth/email/login`,
          { email, password },
          { withCredentials: true },
        );
      }
      await refresh();
      if (redirect) {
        navigate(redirect);
      } else if (isSignup || res.data?.isNew) {
        navigate('/inbox?onboarding=1');
      } else {
        navigate('/inbox');
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.loginFailed'));
    } finally {
      setSubmitting(false);
      setAuthLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {isSignup && (
        <div className="space-y-1 text-sm">
          <label className="block font-medium">{t('auth.nameOptional')}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
          />
        </div>
      )}
      <div className="space-y-1 text-sm">
        <label className="block font-medium">{t('auth.email')}</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
        />
      </div>
      <div className="space-y-1 text-sm">
        <label className="block font-medium">{t('auth.password')}</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="w-full rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60"
      >
        {submitting ? t('auth.submitting') : isSignup ? t('auth.signup') : t('auth.login')}
      </button>
    </form>
  );
}

function ForgotPasswordForm() {
  const { API_V2, setError } = useV2Auth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API_V2}/auth/email/forgot`, { email });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.resetFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1 text-sm">
        <label className="block font-medium">{t('auth.email')}</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="w-full rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60"
      >
        {submitting ? t('auth.submitting') : t('auth.resetSend')}
      </button>
      {done && <p className="text-xs text-[var(--muted)]">{t('auth.resetDone')}</p>}
    </form>
  );
}

export default function AuthV2() {
  const { error, setError, initialLoading, user, refresh } = useV2Auth();
  const { t } = useTranslation();
  const [tab, setTab] = useState('email');
  const [emailMode, setEmailMode] = useState('login');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [fbLoading, setFbLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Internes Redirect-Ziel aus ?redirect= (nur eigene Pfade zulassen)
  const redirectParam = new URLSearchParams(location.search).get('redirect');
  const redirect = redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//') ? redirectParam : null;

  useEffect(() => {
    setError(null);
  }, [tab, emailMode, setError]);

  const startGoogleLogin = () => {
    setGoogleLoading(true);
    const redirectUrl = window.location.origin + (redirect || '/inbox');
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const startFacebookLogin = () => {
    if (!window.FB) return;
    setFbLoading(true);
    window.FB.login((response) => {
      if (response.authResponse) {
        const { accessToken } = response.authResponse;
        axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/v2/auth/facebook/callback`, { accessToken }, { withCredentials: true })
          .then(async (res) => {
            await refresh();
            if (redirect) navigate(redirect);
            else if (res.data?.isNew) navigate('/inbox?onboarding=1');
            else navigate('/inbox');
          })
          .catch(() => setError(t('auth.loginFailed')))
          .finally(() => setFbLoading(false));
      } else {
        setFbLoading(false);
      }
    }, { scope: 'email,public_profile' });
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-sm text-[var(--muted)] bg-white/80 backdrop-blur-sm rounded-xl px-6 py-4">{t('auth.loading')}</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={redirect || '/inbox'} replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white/90 backdrop-blur-sm border border-[var(--border)] rounded-2xl px-6 py-6 shadow-sm">
        <h1 className="text-xl font-semibold mb-4">{t('auth.title')}</h1>
        <p className="text-sm text-[var(--muted)] mb-4">{t('auth.subtitle')}</p>

        <div className="flex gap-2 mb-4 text-xs">
          <button
            type="button"
            onClick={() => setTab('email')}
            className={`flex-1 rounded-full border px-3 py-1 ${
              tab === 'email' ? 'bg-[var(--primary)] text-[var(--primary-contrast)] border-transparent' : 'bg-[var(--surface)]'
            }`}
          >
            {t('auth.email')}
          </button>
          <button
            type="button"
            onClick={() => setTab('google')}
            className={`flex-1 rounded-full border px-3 py-1 ${
              tab === 'google' ? 'bg-[var(--primary)] text-[var(--primary-contrast)] border-transparent' : 'bg-[var(--surface)]'
            }`}
          >
            Google
          </button>
          <button
            type="button"
            onClick={() => setTab('facebook')}
            className={`flex-1 rounded-full border px-3 py-1 ${
              tab === 'facebook' ? 'bg-[var(--primary)] text-[var(--primary-contrast)] border-transparent' : 'bg-[var(--surface)]'
            }`}
          >
            Facebook
          </button>
          <button
            type="button"
            onClick={() => setTab('apple')}
            className={`flex-1 rounded-full border px-3 py-1 ${
              tab === 'apple' ? 'bg-[var(--primary)] text-[var(--primary-contrast)] border-transparent' : 'bg-[var(--surface)]'
            }`}
          >
            Apple
          </button>
        </div>

        {error && <div className="mb-3 text-xs text-[var(--danger)]">{error}</div>}

        {tab === 'email' && (
          <div className="space-y-4">
            <div className="flex gap-1 text-xs mb-1">
              <button type="button" onClick={() => setEmailMode('login')}
                className={`flex-1 rounded-full px-2 py-1 ${emailMode === 'login' ? 'bg-[var(--bg)] font-medium' : ''}`}>
                {t('auth.login')}
              </button>
              <button type="button" onClick={() => setEmailMode('signup')}
                className={`flex-1 rounded-full px-2 py-1 ${emailMode === 'signup' ? 'bg-[var(--bg)] font-medium' : ''}`}>
                {t('auth.signup')}
              </button>
              <button type="button" onClick={() => setEmailMode('forgot')}
                className={`flex-1 rounded-full px-2 py-1 ${emailMode === 'forgot' ? 'bg-[var(--bg)] font-medium' : ''}`}>
                {t('auth.forgot')}
              </button>
            </div>
            {emailMode === 'forgot' ? <ForgotPasswordForm /> : <EmailAuthForm mode={emailMode} redirect={redirect} />}
          </div>
        )}

        {tab === 'google' && (
          <div className="space-y-4">
            <p className="text-xs text-[var(--muted)]">{t('auth.googleDesc')}</p>
            <button
              type="button"
              onClick={startGoogleLogin}
              disabled={googleLoading}
              data-testid="google-login-btn"
              className="w-full rounded-full border border-[var(--border)] bg-white py-2.5 text-sm font-medium hover:bg-gray-50 disabled:opacity-60 flex items-center justify-center gap-3"
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {googleLoading ? t('auth.googleRedirect') : t('auth.googleBtn')}
            </button>
          </div>
        )}

        {tab === 'facebook' && (
          <div className="space-y-4">
            <p className="text-xs text-[var(--muted)]">{t('auth.facebookDesc', { defaultValue: 'Melde Dich mit Deinem Facebook-Konto an.' })}</p>
            <button
              type="button"
              onClick={startFacebookLogin}
              disabled={fbLoading || !window.FB}
              data-testid="facebook-login-btn"
              className="w-full rounded-full bg-[#1877F2] text-white py-2.5 text-sm font-medium hover:bg-[#166FE5] disabled:opacity-60 flex items-center justify-center gap-3"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              {fbLoading ? t('auth.facebookLoading') : t('auth.facebookLogin')}
            </button>
            {!window.FB && <p className="text-[10px] text-amber-600">{t('auth.facebookNotConfigured', { defaultValue: 'Facebook Login wird noch konfiguriert.' })}</p>}
          </div>
        )}

        {tab === 'apple' && (
          <div className="space-y-3">
            <p className="text-xs text-[var(--muted)]">{t('auth.appleDesc')}</p>
            <button
              type="button"
              disabled
              className="w-full rounded-full border border-[var(--border)] bg-[var(--surface)] py-2 text-sm font-medium text-[var(--muted)] cursor-not-allowed"
            >
              {t('auth.appleBtn')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
